import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { getUserFacingErrorMessage } from '../utils/api';
import { UserContext } from '../App';
import NomineeShell from './NomineeShell';
import { Button, Card, Container, Stack, Text } from '../ui';
import NomineeEstateTabs from './NomineeEstateTabs';
import NomineeEstateHeading from './NomineeEstateHeading';
import { useNomineeEstates } from '../hooks/useNomineeEstates';

export default function NomineeEstateFinancial() {
  const { currentUser } = useContext(UserContext);
  const { ownerSub } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const { estates } = useNomineeEstates();
  const estate = estates.find((e) => e?.ownerSub === ownerSub);
  const finGranted = !!estate?.grants?.financial_summary;

  useEffect(() => {
    if (!finGranted) {
      setLoading(false);
      setRows([]);
      setError('');
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setError('');
        const resp = await api.get('/nominee/financial-summary', { params: { ownerSub } });
        setRows(Array.isArray(resp.data) ? resp.data : []);
      } catch (e) {
        setError(getUserFacingErrorMessage(e, {
          fallback: 'We could not load the financial summary. Please try again.',
          includeSupportForUnknown: false,
        }));
      } finally {
        setLoading(false);
      }
    })();
  }, [ownerSub, finGranted]);

  if (!currentUser) return <div>Loading...</div>;
  if (currentUser.type !== 'nominee') {
    navigate('/dashboard');
    return null;
  }

  return (
    <NomineeShell>
      <div style={{ padding: '32px 0' }}>
        <Container>
          <Stack gap={16}>
            <NomineeEstateHeading ownerSub={ownerSub} title="Financial summary" />

            <NomineeEstateTabs ownerSub={ownerSub} active="financial" />

            <div>
              <Button type="button" variant="secondary" onClick={() => navigate('/nominee')}>
                Back to estates
              </Button>
            </div>

            {!finGranted && !loading && (
              <Card style={{ background: 'var(--peach-50)', borderColor: 'var(--peach-300)' }}>
                <Text style={{ margin: 0 }}>
                  The account owner has not shared their financial summary with you yet.
                  Ask them to enable sharing under Nominees in their ProVault account.
                </Text>
              </Card>
            )}

            {error && (
              <Card style={{ background: 'var(--peach-50)', borderColor: 'var(--peach-300)' }}>
                <div className="pv-error">{error}</div>
              </Card>
            )}

            {finGranted && loading ? (
              <Text>Loading…</Text>
            ) : finGranted ? (
              <Card>
                <Stack gap={12}>
                  <Text as="div" weight={700}>Assets (redacted)</Text>
                  {rows.length === 0 ? (
                    <Text muted>No shared financial assets.</Text>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: 'var(--mist-100)' }}>
                            <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid var(--mist-200)' }}>Category</th>
                            <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid var(--mist-200)' }}>Type</th>
                            <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid var(--mist-200)' }}>Institution</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.id}>
                              <td style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>{r.typeCategory || ''}</td>
                              <td style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>{r.type || ''}</td>
                              <td style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>{r.institution || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Stack>
              </Card>
            ) : null}
          </Stack>
        </Container>
      </div>
    </NomineeShell>
  );
}


